import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { DisplayDateTime } from "components/DisplayDate";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getOriginalFileName } from "utils/utils";

const FIELD_LABELS = {
  // Applicant details
  name: "Full Name",
  email: "Email",
  number: "Phone",
  dob: "Date of Birth",
  gender: "Gender",
  passportNumber: "Passport Number",
  passportExpiry: "Passport Expiry",
  visaStatus: "Visa Status",
  maritalStatus: "Marital Status",
  supportingDocuments: "Supporting Documents",
  // Placement
  course: "Course",
  university: "University",
  state: "State",
  intake: "Intake",
  fee: "Fee (per semester)",
  caseOfficer: "Case Officer",
  reference: "Referral",
  agentName: "Agent Name",
  agreedFeePerSemester: "Agreed Fee / Semester",
  agreedDiscount: "Agreed Discount (%)",
  // Offer applied
  offerLetterApplied: "Offer Letter Applied",
  offerLetterAppliedDate: "Offer Applied Date",
  offerLetterAppliedTo: "Offer Applied To",
  offerLetterAppliedNotes: "Offer Applied Notes",
  offerLetterDocs: "Offer Letter Documents",
  // Offer received
  courseStartDate: "Course Start Date",
  courseEndDate: "Course End Date",
  studentId: "Student ID",
  offerReceivedDocs: "Offer Received Documents",
  // Signed offer & COE
  signedOfferDate: "Signed Offer Date",
  signedOfferNotes: "Signed Offer Notes",
  signedOfferDocs: "Signed Offer Documents",
  coeFeeReceiptDocs: "COE Fee Receipts",
  coeAdditionalDocs: "Additional Documents",
  coeFeeAmount: "COE Fee Amount",
  coeFeeDate: "COE Fee Date",
  coeFeeReference: "COE Fee Reference",
  coeApplied: "COE Applied",
  coeAppliedDate: "COE Applied Date",
  coeAppliedTo: "COE Applied To",
  coeAppliedNotes: "COE Applied Notes",
  coeAppliedDocs: "COE Application Documents",
  coeReceived: "COE Received",
  coeReceivedDate: "COE Received Date",
  coeNumber: "COE Number",
  coeReceivedNotes: "COE Received Notes",
  coeReceivedDocs: "COE Received Documents",
  // GS step
  gteNeeded: "GS Required",
  gteApplied: "GS Applied",
  gteAppliedDate: "GS Applied Date",
  gteApproved: "GS Approved",
  gteApprovedDate: "GS Approved Date",
  gteNotes: "GS Notes",
  gteDocs: "GS Documents",
};

const getEventDate = (e) => e?.changeDate || e?.createdDate;
const getNewStatus = (e) =>
  e?.eventLabel || e?.derivedStatus || e?.newStatus || e?.status || "Updated";
const getChangedBy = (e) => e?.changedByName || e?.userName || e?.changedBy;

const getDocumentUrl = (event) =>
  event?.documentUrl || event?.attachment || event?.document || event?.fileUrl || event?.file;

const getDotColor = (eventType) => {
  if (eventType === "CREATION") return "bg-green-500";
  if (eventType === "PHASE_ADVANCE") return "bg-orange-500";
  if (eventType === "FIELD_UPDATE") return "bg-blue-500";
  if (eventType === "MANUAL_NOTE") return "bg-gray-400";
  return "bg-orange-500";
};

const ChangesDiff = ({ changes = [] }) => {
  if (!changes || changes.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {changes.map((change, i) => (
        <li key={i}>
          <Typography variant="caption" color="text.secondary">
            <span className="font-medium text-app-text">
              {FIELD_LABELS[change.field] ?? change.field}:
            </span>{" "}
            <span className="line-through text-app-text-muted">{change.oldValue ?? "—"}</span>
            {" → "}
            <span className="text-app-text">{change.newValue ?? "—"}</span>
          </Typography>
        </li>
      ))}
    </ul>
  );
};

const TimelineEntry = ({ event, idx }) => {
  const [expanded, setExpanded] = useState(false);

  const docUrl = getDocumentUrl(event);
  const newStatus = getNewStatus(event);
  const hasChanges = Array.isArray(event.changes) && event.changes.length > 0;
  const dotColor = getDotColor(event.eventType);

  return (
    <li
      key={event.id || idx}
      className="relative pb-6 pl-6 border-l-2 border-app-border ml-2 last:pb-0"
    >
      <span
        className={`absolute -left-[7px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${dotColor}`}
      />
      <Typography variant="subtitle2" className="text-app-text">
        {newStatus}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        <DisplayDateTime value={getEventDate(event)} />
        {getChangedBy(event) ? ` · ${getChangedBy(event)}` : ""}
      </Typography>
      {event.notes && (
        <Typography variant="body2" className="mt-1 text-app-text-secondary">
          {event.notes}
        </Typography>
      )}
      {hasChanges && (
        <button
          type="button"
          className="mt-1 text-xs text-primary hover:text-blue-800 focus:outline-none"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "▾ Hide details" : "▸ Details"}
        </button>
      )}
      {expanded && hasChanges && <ChangesDiff changes={event.changes} />}
      {docUrl && (
        <Paper
          variant="outlined"
          className="mt-2 p-2 flex items-center gap-2 border-app-border bg-app-surface-muted/80 max-w-md"
        >
          <AttachFileOutlinedIcon fontSize="small" className="text-app-text-muted" />
          <span className="text-sm text-app-text truncate flex-1">
            {getOriginalFileName(docUrl)}
          </span>
          <Tooltip title="Preview">
            <IconButton component={Link} to={docUrl} target="_blank" size="small" color="primary">
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton component={Link} to={docUrl} download target="_blank" size="small">
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
    </li>
  );
};

const AdmissionStatusHistory = ({ admissionId, currentStatus }) => {
  const { data: events = [], isFetching } = useQuery({
    queryKey: ["student-status-history", admissionId],
    queryFn: async () => {
      try {
        const res = await axios.get(`${API_URL}/student/${admissionId}/change-history`);
        return res.data?.data ?? res.data ?? [];
      } catch {
        return [];
      }
    },
    enabled: !!admissionId,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  const sorted = [...events].sort(
    (a, b) => new Date(getEventDate(b) || 0) - new Date(getEventDate(a) || 0)
  );

  const displayedStatus = sorted[0]?.derivedStatus || currentStatus;

  return (
    <div className="space-y-6">
      <div>
        <Typography variant="h6" className="text-app-text font-semibold">
          Status history
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mt-1 max-w-3xl">
          Changes appear here automatically each time the application form is saved.
        </Typography>
      </div>

      {displayedStatus && (
        <div className="flex items-center gap-2">
          <Typography variant="subtitle2" color="text.secondary">
            Current status:
          </Typography>
          <Chip label={displayedStatus} size="small" color="primary" variant="outlined" />
        </div>
      )}

      {isFetching ? (
        <div className="flex justify-center py-12">
          <CircularProgress />
        </div>
      ) : sorted.length === 0 ? (
        <Card className="p-6 text-center border-app-border">
          <Typography variant="body2" color="text.secondary">
            No history yet. Changes appear here automatically when the application form is saved.
          </Typography>
        </Card>
      ) : (
        <div>
          <Typography variant="subtitle1" className="font-semibold text-app-text mb-3">
            Timeline
          </Typography>
          <ul className="space-y-0">
            {sorted.map((event, idx) => (
              <TimelineEntry key={event.id || idx} event={event} idx={idx} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdmissionStatusHistory;
